/**
 * Currency locale mappings for different currency codes
 * Maps currency codes to their appropriate locale and formatter
 */
const CURRENCY_LOCALES: Record<string, { locale: string; symbol?: string }> = {
  CLP: { locale: 'es-CL', symbol: '$' },
  UF: { locale: 'es-CL', symbol: 'UF' },
  COP: { locale: 'es-CO', symbol: '$' },
  PEN: { locale: 'es-PE', symbol: 'S/' },
  ARS: { locale: 'es-AR', symbol: '$' },
  MXN: { locale: 'es-MX', symbol: '$' },
  USD: { locale: 'en-US', symbol: '$' },
  EUR: { locale: 'es-ES', symbol: '€' },
};

/**
 * Formats a currency value with the appropriate symbol and locale
 * @param amount - The numeric amount to format
 * @param currency - The currency code (CLP, UF, USD, EUR, etc.)
 * @param decimals - Number of decimal places to display (default: 2)
 * @returns Formatted currency string or placeholder if amount is missing
 *
 * @example
 * formatCurrency(1500, 'CLP')           // "$ 1.500"
 * formatCurrency(45.99, 'USD')          // "$ 45.99"
 * formatCurrency(100, 'UF', 0)          // "UF 100"
 */
export const formatCurrency = (
  amount: string | number | null | undefined,
  currency: string,
  decimals = 2
): string => {
  if (amount === null || amount === undefined || amount === '') {
    return '—';
  }

  const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;

  if (Number.isNaN(numAmount)) {
    return '—';
  }

  const config = CURRENCY_LOCALES[currency];

  if (config) {
    try {
      // Use Intl.NumberFormat for proper locale-aware formatting
      const formatter = new Intl.NumberFormat(config.locale, {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
        useGrouping: true,
      });

      const formatted = formatter.format(numAmount);
      const symbol = config.symbol ?? currency;

      return `${symbol} ${formatted}`;
    } catch {
      // Fallback if Intl.NumberFormat fails
      return `${currency} ${numAmount.toFixed(decimals)}`;
    }
  }

  // Fallback for unknown currencies
  return `${currency} ${numAmount.toFixed(decimals)}`;
};
